import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Check, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { SIZES, priceFor } from "@/lib/pricing";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0b0f17]">
      <Navbar />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16" data-testid="services-list-section">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Our Packages</div>
        <h1 className="font-display mt-2 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-slate-100">
          Every finish, dialed in.
        </h1>
        <p className="mt-4 text-slate-400 max-w-2xl">
          Transparent pricing. No hidden fees. Flat $25 deposit on every booking — the balance is paid on completion.
        </p>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl" data-testid="services-price-varies-note">
          Prices are a guide and may vary based on your vehicle's size and condition. Full details in our{" "}
          <Link to="/terms" className="text-[#ff6b00] underline">Terms &amp; Conditions</Link>.
        </p>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-2">
          {services.map((s, i) => (
            <div
              key={s.id}
              className="cj-card overflow-hidden flex flex-col lg:flex-row"
              data-testid={`service-card-${s.slug}`}
            >
              <div className="lg:w-2/5 relative aspect-[4/3] lg:aspect-auto overflow-hidden">
                {s.image_url ? (
                  <img src={s.image_url} alt={s.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#1a2234] to-[#121824]" />
                )}
                <div className="absolute top-3 left-3 cj-pill bg-[#0b0f17]/80 border border-white/10 text-slate-200">
                  <Clock className="h-3 w-3 text-[#ff6b00]" /> {s.duration_minutes}m
                </div>
              </div>
              <div className="lg:w-3/5 p-6 flex flex-col">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display uppercase font-extrabold text-2xl text-slate-100 leading-tight">
                    {s.name}
                  </div>
                  {s.slug === "full-detail" && (
                    <span data-testid="services-most-popular-badge" className="cj-pill bg-[#ff6b00]/15 text-[#ff6b00] border border-[#ff6b00]/40 text-[10px]">★ Most popular</span>
                  )}
                </div>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.description}</p>
                <ul className="mt-4 space-y-2">
                  {s.features?.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="h-4 w-4 text-[#10b981] shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {s.prerequisite && (
                  <div className="mt-4 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200" data-testid={`service-prerequisite-${s.slug}`}>
                    <span className="font-display uppercase font-bold">Requirement:</span> {s.prerequisite}
                  </div>
                )}
                {Object.keys(s.prices || {}).length > 0 && !s.quote_only && (
                  <div className="mt-4 grid grid-cols-4 gap-2" data-testid={`service-size-prices-${s.slug}`}>
                    {SIZES.map((z) => (
                      <div key={z.v} className="rounded-lg bg-[#0f1522] border border-white/5 px-2 py-2 text-center">
                        <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500 leading-tight">{z.l}</div>
                        <div className="font-display font-extrabold text-[#ff6b00] mt-1">${priceFor(s, z.v).toFixed(0)}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between">
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                      {s.quote_only ? "Quote only · from" : "From"}
                    </div>
                    <div className="font-display font-extrabold text-3xl text-[#ff6b00]">
                      ${s.price.toFixed(0)}
                    </div>
                  </div>
                  <Link
                    to={`/book?service=${s.id}`}
                    data-testid={`service-book-${s.slug}`}
                    className="cj-btn-primary px-5 py-3 rounded-full inline-flex items-center gap-1"
                  >
                    Select <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
